import { ChangeEvent, useState } from 'react';

import { useAuth } from '@/hooks/use-auth';
import { forgotPassword } from '@/lib/auth/auth-service';

import ConfirmationMessage from '../components/common/ConfirmationMessage';
import type { ErrorMessage } from '../helpers/type';
import { emailChecker } from '../helpers/utils';
import { useTranslation } from '../hooks/useTranslation';
import ForgotPasswordForm from './ForgotPasswordForm';

interface ResetInput {
  email: string;
  inputError: {
    email: ErrorMessage;
  };
  border: {
    email: string;
  };
}

const ForgotPassword = () => {
  const { t } = useTranslation();
  const { isLoading } = useAuth();
  const [loading, setLoading] = useState(isLoading);
  const [userFound, setUserFound] = useState(false);
  const [resetInput, setResetInput] = useState<ResetInput>({
    email: '',
    inputError: {
      email: null,
    },
    border: {
      email: 'border-gray-300',
    },
  });

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setResetInput((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async () => {
    const email = emailChecker(resetInput.email);

    if (!email.isValid) {
      setResetInput((prev) => ({
        ...prev,
        inputError: {
          email: 'Email is incorrect',
        },
        border: {
          email: 'border-red-500',
        },
      }));
      return;
    }

    if (resetInput.email && email.isValid) {
      setLoading(true);
      const result = await forgotPassword(resetInput.email);

      if (!result.success) {
        setResetInput((prev) => ({
          ...prev,
          inputError: {
            email: "Email didn't exist please register",
          },
          border: {
            email: 'border-red-500',
          },
        }));
        setLoading(false);
        return;
      }

      setUserFound(true);
      setLoading(false);
    }
  };
  return !userFound ? (
    <ForgotPasswordForm
      email={resetInput.email}
      emailError={resetInput.inputError.email}
      emailBorder={resetInput.border.email}
      loading={loading}
      onInputChange={onInputChange}
      onSubmit={handleSubmit}
      title={t('restoreYourAccount')}
      subtitle={t('restoreEmailTitle')}
      emailLabel={t('emailAddress')}
      submitLabel={t('resetPassword')}
    />
  ) : (
    <ConfirmationMessage
      title={t('restorEmailSent')}
      message={t('confirmationMessage.forgotPassword')}
    />
  );
};

export default ForgotPassword;
